import React from 'react';
import Button from '@app/react-common/components/button/Button';
import Tooltip from '@app/react-common/components/tooltip/Tooltip';
import { PriceRangeTier } from '../BulkEditModal';
import { ShippingThresholdsFormData } from './ShippingThresholdsEditView';
import { CollapsibleSection } from '../components';
import { BasePreviewViewProps, SelectedMethodInfo } from './types';

interface ShippingThresholdsPreviewViewProps extends BasePreviewViewProps {
  formData: ShippingThresholdsFormData;
  currencySign: string;
}

export const ShippingThresholdsPreviewView = ({
  onBack,
  onClose,
  onSaveChanges,
  formData,
  selectedMethods,
  currencySign,
}: ShippingThresholdsPreviewViewProps) => {
  const adjustMinValue = (value: number | string | null | undefined): number => {
    const effectiveValue = value === null || value === undefined ? 0 : Number(value);
    if (formData.keepZeroAsMinimum && effectiveValue === 0) {
      return 0;
    }
    const newValue = effectiveValue + formData.adjustBy;
    if (newValue < 0) {
      return 0;
    }
    return newValue;
  };

  const adjustMaxValue = (value: number | string | null | undefined): number | null => {
    if (value === null || value === undefined) {
      return null;
    }
    const numValue = Number(value);
    // 0 means unlimited for max, never adjust it
    if (numValue === 0) {
      return 0;
    }
    const newValue = numValue + formData.adjustBy;
    if (newValue <= 0) {
      return 0;
    }
    return newValue;
  };

  const formatMinPrice = (value: number | null | undefined): string => {
    const effectiveValue = value ?? 0;
    return `${currencySign}${Number(effectiveValue).toFixed(2)}`;
  };

  const formatMaxPrice = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number(value) === 0) {
      return 'No limit';
    }
    return `${currencySign}${Number(value).toFixed(2)}`;
  };

  const getAllTiers = (method: SelectedMethodInfo): PriceRangeTier[] => {
    const tiers: PriceRangeTier[] = [];

    if (
      method.minOrderPriceRange !== undefined ||
      method.maxOrderPriceRange !== undefined ||
      method.rate !== undefined
    ) {
      tiers.push({
        minOrderPriceRange: method.minOrderPriceRange ?? null,
        maxOrderPriceRange: method.maxOrderPriceRange ?? null,
        rate: method.rate ?? 0,
      });
    }

    if (method.additionalPriceRanges && Array.isArray(method.additionalPriceRanges)) {
      tiers.push(...method.additionalPriceRanges);
    }

    return tiers;
  };

  const getAdjustedTiers = (tiers: PriceRangeTier[]): PriceRangeTier[] => {
    return tiers.map((tier) => ({
      ...tier,
      minOrderPriceRange: adjustMinValue(tier.minOrderPriceRange),
      maxOrderPriceRange: adjustMaxValue(tier.maxOrderPriceRange),
    }));
  };

  const formatTierRange = (tier: PriceRangeTier): string => {
    const min = formatMinPrice(tier.minOrderPriceRange);
    const max = formatMaxPrice(tier.maxOrderPriceRange);
    return `${min} - ${max}`;
  };

  const hasTierChanges = (original: PriceRangeTier, adjusted: PriceRangeTier): boolean => {
    const originalMin = original.minOrderPriceRange ?? 0;
    const adjustedMin = adjusted.minOrderPriceRange ?? 0;
    return originalMin !== adjustedMin || original.maxOrderPriceRange !== adjusted.maxOrderPriceRange;
  };

  const methodHasChanges = (method: SelectedMethodInfo): boolean => {
    const originalTiers = getAllTiers(method);
    if (originalTiers.length === 0) return false;
    const adjustedTiers = getAdjustedTiers(originalTiers);
    return originalTiers.some((orig, idx) => hasTierChanges(orig, adjustedTiers[idx]));
  };

  const changedMethods = selectedMethods.filter(methodHasChanges);
  const unchangedMethods = selectedMethods.filter((m) => !methodHasChanges(m));
  const affectedCount = changedMethods.length;
  const isSaveDisabled = affectedCount === 0;

  return (
    <div className="bulk-edit-modal__view">
      <div className="bulk-edit-modal__header bulk-edit-modal__header--with-subtitle">
        <div className="bulk-edit-modal__header__left">
          <div className="bulk-edit-modal__header__titles">
            <h2>Confirm changes</h2>
            <span className="bulk-edit-modal__header__subtitle">
              {affectedCount} of {selectedMethods.length} Methods Will Be Affected
            </span>
          </div>
        </div>
        <Button icon="close" variant="ghost" size="small" onClick={onClose} />
      </div>
      <div className="bulk-edit-modal__content">
        <div className="bulk-edit-modal__summary">
          <span className="bulk-edit-modal__summary__label">Adjustment:</span>
          <span className="bulk-edit-modal__summary__value">
            {formData.adjustBy >= 0 ? '+' : ''}
            {currencySign}
            {formData.adjustBy.toFixed(2)}
          </span>
          {formData.keepZeroAsMinimum && <span className="bulk-edit-modal__summary__note">(keeping 0 as minimum)</span>}
        </div>
        {changedMethods.length > 0 && (
          <CollapsibleSection title="Changed" count={changedMethods.length} defaultOpen={true}>
            {changedMethods.map((method) => {
              const originalTiers = getAllTiers(method);
              const adjustedTiers = getAdjustedTiers(originalTiers);

              return (
                <div key={method.id} className="bulk-edit-modal__preview-item">
                  <div className="bulk-edit-modal__preview-item__header">{method.name || '(No name)'}</div>
                  <div className="bulk-edit-modal__preview-item__group">{method.groupName}</div>
                  {originalTiers.map((originalTier, index) => {
                    const adjustedTier = adjustedTiers[index];
                    const hasChange = hasTierChanges(originalTier, adjustedTier);

                    return (
                      <div key={index} className="bulk-edit-modal__preview-item__change">
                        <span className="bulk-edit-modal__preview-item__change__label">
                          Tier {index + 1} ({currencySign}
                          {Number(originalTier.rate ?? 0).toFixed(2)}):
                        </span>
                        {hasChange ? (
                          <>
                            <span className="bulk-edit-modal__preview-item__change__old">
                              {formatTierRange(originalTier)}
                            </span>
                            <span className="bulk-edit-modal__preview-item__change__arrow">&gt;</span>
                            <span className="bulk-edit-modal__preview-item__change__new">
                              {formatTierRange(adjustedTier)}
                            </span>
                          </>
                        ) : (
                          <span className="bulk-edit-modal__preview-item__change__unchanged">
                            {formatTierRange(originalTier)} (unchanged)
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </CollapsibleSection>
        )}
        {unchangedMethods.length > 0 && (
          <CollapsibleSection title="Unchanged" count={unchangedMethods.length} defaultOpen={false}>
            {unchangedMethods.map((method) => {
              const originalTiers = getAllTiers(method);

              if (originalTiers.length === 0) {
                return (
                  <div key={method.id} className="bulk-edit-modal__preview-item">
                    <div className="bulk-edit-modal__preview-item__header">{method.name || '(No name)'}</div>
                    <div className="bulk-edit-modal__preview-item__group">{method.groupName}</div>
                    <div className="bulk-edit-modal__preview-item__change">
                      <span className="bulk-edit-modal__preview-item__change__unchanged">
                        No price ranges configured
                      </span>
                    </div>
                  </div>
                );
              }

              return (
                <div key={method.id} className="bulk-edit-modal__preview-item">
                  <div className="bulk-edit-modal__preview-item__header">{method.name || '(No name)'}</div>
                  <div className="bulk-edit-modal__preview-item__group">{method.groupName}</div>
                  {originalTiers.map((originalTier, index) => (
                    <div key={index} className="bulk-edit-modal__preview-item__change">
                      <span className="bulk-edit-modal__preview-item__change__label">
                        Tier {index + 1} ({currencySign}
                        {Number(originalTier.rate ?? 0).toFixed(2)}):
                      </span>
                      <span className="bulk-edit-modal__preview-item__change__unchanged">
                        {formatTierRange(originalTier)} (unchanged)
                      </span>
                    </div>
                  ))}
                </div>
              );
            })}
          </CollapsibleSection>
        )}
      </div>
      <div className="bulk-edit-modal__footer">
        <Button text="Back" onClick={onBack} size="medium" variant="secondary" />
        {isSaveDisabled ? (
          <Tooltip tooltipText="No methods will be affected by this change" maxWidth={220}>
            <Button
              text="Save Changes"
              onClick={() => onSaveChanges(changedMethods.map((m) => m.id))}
              size="medium"
              variant="primary"
              disabled
            />
          </Tooltip>
        ) : (
          <div className="bulk-edit-modal__button-wrapper">
            <Button
              text="Save Changes"
              onClick={() => onSaveChanges(changedMethods.map((m) => m.id))}
              size="medium"
              variant="primary"
            />
          </div>
        )}
      </div>
    </div>
  );
};
